import { camelizeKeys } from 'humps';
import Cookies from 'js-cookie';
import { useNavigate } from 'react-router-dom';
import { z } from 'zod';

import { COOKIE_KEY_TOKEN, ROUTES } from '@/constants';
import { useAnalytics } from '@/hooks/useAnalytics';
import UserSchema from '@/modules/auth/userSchema';
import { USER_ROLES } from '@/roles';
import { userLogout } from '@/services/authService';
import parseJwt from '@/utils/parseJwt';

type UserPayload = z.infer<typeof UserSchema>;

type AuthState = {
  user: UserPayload | null;
  token: string | null;
  isAuthenticated: boolean;
  login: (token: string) => Promise<void>;
  logout: () => Promise<void>;
  isSysAdmin: boolean;
  isFleetAdmin: boolean;
  isAppCustomer: boolean;
  isPeriodsFilterEnabled: boolean;
};

type User = z.infer<typeof UserSchema>;

const useAuth = (): AuthState => {
  const navigate = useNavigate();
  const analytics = useAnalytics();

  const token = Cookies.get(COOKIE_KEY_TOKEN) as string | null;
  const isAuthenticated = Boolean(token);
  const user = token ? (camelizeKeys(parseJwt(token)) as User) : null;
  const isTokenExpired = user?.exp ? Date.now() >= user.exp * 1000 : false;
  const isSysAdmin = user?.roles.includes(USER_ROLES.SYS_ADMIN) ?? false;
  const isFleetAdmin = user?.roles.includes(USER_ROLES.ADMINISTRATOR) ?? false;
  const isAppCustomer = user?.isFairmaticAppEnabled ?? false;
  const isPeriodsFilterEnabled = user?.isFairmaticAppEnabled === false;

  const login = async (newToken: string) => {
    Cookies.set(COOKIE_KEY_TOKEN, newToken);
    const newUser = camelizeKeys(parseJwt(newToken)) as User;
    analytics.setupUser(newUser);
    await navigate(ROUTES.OVERVIEW);
  };

  const logout = async () => {
    const success = await userLogout();
    if (success) {
      Cookies.remove(COOKIE_KEY_TOKEN);
    }
    await navigate(ROUTES.LOGIN);
  };

  if (isTokenExpired) {
    // eslint-disable-next-line no-void
    void logout();
  }

  return {
    token,
    isAuthenticated,
    user,
    login,
    logout,
    isSysAdmin,
    isFleetAdmin,
    isAppCustomer,
    isPeriodsFilterEnabled,
  };
};

export default useAuth;
